import { typographyTokens } from '../../theme/typographyTokens';
import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Checkbox,
  Box,
  Button,
  useTheme,
} from '@mui/material';
import { HeadCell } from './SepTableBody';
import { useDispatch } from 'react-redux';
import { setSnackbar } from '../../services/appSlice/appSlice';

const modalTextSize = '15.4px';

const EditColumnModal = ({
  visibleColumns,
  setVisibleColumns,
  closeModal,
  defaultColumns,
  isModalOpen
}: {
  visibleColumns: HeadCell[];
  setVisibleColumns: (cols: HeadCell[]) => void;
  closeModal: () => void;
  defaultColumns: HeadCell[];
  isModalOpen: boolean;
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  // Local state to track changes before update
  const [localColumns, setLocalColumns] = useState<HeadCell[]>(visibleColumns);

  // Sync local state if visibleColumns prop changes
  React.useEffect(() => {
    setLocalColumns(visibleColumns);
  }, [visibleColumns]);

  // Reset localColumns when modal closes
  React.useEffect(() => {
    if (!isModalOpen) {
      setLocalColumns(visibleColumns);
    }
  }, [isModalOpen, visibleColumns]);

  return (
    <Box
      sx={{
        p: 3,
        maxHeight: '500px',
        overflow: 'auto',
        color: theme.palette.content.default,
        backgroundColor: theme.palette.content.white,
      }}
    >
      <Table sx={{ width: '100%', '& .MuiTableCell-root': { borderColor: theme.palette.elements.divider } }} aria-labelledby="tableTitle" size="medium">
        <TableHead>
          <TableRow sx={{ backgroundColor: theme.palette.supportingAdditional.tealUltraLight }}>
            <TableCell sx={{ color: theme.palette.content.default, fontSize: modalTextSize, fontWeight: typographyTokens.fontWeights.bold }}>No</TableCell>
            <TableCell sx={{ color: theme.palette.content.default, fontSize: modalTextSize, fontWeight: typographyTokens.fontWeights.bold }}>Column Name</TableCell>
            <TableCell sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', color: theme.palette.content.default, fontSize: modalTextSize, fontWeight: typographyTokens.fontWeights.bold }}>Visible</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {localColumns.map((headCell, index) => {
            return (
              <TableRow key={index}>
                <TableCell sx={{ color: theme.palette.content.default, fontSize: modalTextSize }}>{index + 1}</TableCell>
                <TableCell sx={{ color: theme.palette.content.default, fontSize: modalTextSize }}>{headCell.label}</TableCell>
                <TableCell sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                  <Checkbox
                    checked={localColumns[index].visible}
                    sx={{ color: theme.palette.prime.blue, '&.Mui-checked': { color: theme.palette.prime.blue } }}
                    onChange={(e) => {
                      const columns = localColumns.map((col) => {
                        if (col.id === headCell.id)
                          return {
                            ...col,
                            visible: e.target.checked,
                          };
                        else {
                          return col;
                        }
                      });
                      setLocalColumns(columns);
                    }}
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, gap: 2 }}>
        <Button
          variant="outlined"
          onClick={() => {
            setVisibleColumns(defaultColumns);
            closeModal();
            dispatch(setSnackbar({ type: 'success', text: 'Table columns have been reset to the default view.' }));
            setTimeout(() => {
              dispatch(setSnackbar(null));
            }, 6000);
          }}
          sx={{ px: 2.5, py: 1, fontSize: modalTextSize, fontWeight: typographyTokens.fontWeights.bold, borderRadius: '4px', color: theme.palette.content.default }}
        >
          Reset
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            setVisibleColumns(localColumns);
            closeModal();
            dispatch(setSnackbar({ type: 'success', text: 'Table column display has been updated.' }));
            setTimeout(() => {
              dispatch(setSnackbar(null));
            }, 7000);
          }}
          sx={{ px: 2.5, py: 1, fontSize: modalTextSize, fontWeight: typographyTokens.fontWeights.bold, borderRadius: '4px' }}
        >
          Update
        </Button>
      </Box>
    </Box>
  );
};

export default EditColumnModal;
